import type {
  ImportSessionUpdateReason,
  ImportSessionUpdatedEvent,
} from '@digital-twins/shared-types'

const FLOW_SNAPSHOT_PROGRESS_ONLY_REASONS = new Set<ImportSessionUpdateReason>([
  'analyze_started',
  'analyze_dispatched',
  'analyze_progress',
  'create_progress',
  'preview_refresh_started',
  'post_processing_updated',
  'context_materials_indexing_updated',
])

const PREVIEW_PROJECTION_REASONS = new Set<ImportSessionUpdateReason>([
  'session_created',
  'session_updated',
  'session_expired',
  'analyze_started',
  'analyze_dispatched',
  'analyze_progress',
  'analyze_completed',
  'analyze_failed',
  'analyze_cancelled',
  'analyze_restarted',
  'preview_step_completed',
  'preview_refresh_started',
  'preview_shuffled',
  'preview_hydration_completed',
  'preview_twin_enriched',
  'create_enqueued',
  'create_completed',
  'create_failed',
  'session_discarded',
  'context_materials_entered',
  'context_materials_completed',
  'context_materials_updated',
  'context_materials_file_added',
  'context_materials_file_removed',
  'context_materials_retry_requested',
  'context_materials_cleanup_completed',
  'socket_resynced_after_gap',
  'visibility_resynced_after_gap',
  'socket_subscribed_sync',
])

const STATUS_PROJECTION_REASONS = new Set<ImportSessionUpdateReason>([
  'session_created',
  'session_updated',
  'session_expired',
  'analyze_started',
  'analyze_dispatched',
  'analyze_progress',
  'analyze_completed',
  'analyze_failed',
  'analyze_cancelled',
  'analyze_restarted',
  'preview_step_completed',
  'preview_refresh_started',
  'preview_hydration_completed',
  'preview_twin_enriched',
  'create_enqueued',
  'create_completed',
  'create_failed',
  'post_processing_updated',
  'session_discarded',
  'context_materials_entered',
  'context_materials_completed',
  'context_materials_updated',
  'context_materials_file_added',
  'context_materials_file_removed',
  'context_materials_retry_requested',
  'context_materials_indexing_updated',
  'context_materials_cleanup_completed',
  'socket_resynced_after_gap',
  'visibility_resynced_after_gap',
  'socket_subscribed_sync',
])

const CONTEXT_MATERIALS_PROJECTION_REASONS = new Set<ImportSessionUpdateReason>([
  'session_created',
  'session_updated',
  'session_expired',
  'session_discarded',
  'context_materials_entered',
  'context_materials_completed',
  'context_materials_updated',
  'context_materials_file_added',
  'context_materials_file_removed',
  'context_materials_retry_requested',
  'context_materials_indexing_updated',
  'context_materials_cleanup_completed',
  'socket_resynced_after_gap',
  'visibility_resynced_after_gap',
  'socket_subscribed_sync',
])

const WORKBOOK_PROJECTION_REASONS = new Set<ImportSessionUpdateReason>([
  'session_created',
  'session_updated',
  'session_expired',
  'analyze_started',
  'analyze_dispatched',
  'analyze_restarted',
  'session_discarded',
  'socket_resynced_after_gap',
  'visibility_resynced_after_gap',
  'socket_subscribed_sync',
])

const COALESCED_MACHINE_PROGRESS_REASONS =
  FLOW_SNAPSHOT_PROGRESS_ONLY_REASONS

export function shouldRevalidateFlowSnapshotForImportSessionUpdate(
  event: Pick<ImportSessionUpdatedEvent, 'reason'>,
) {
  return !FLOW_SNAPSHOT_PROGRESS_ONLY_REASONS.has(event.reason)
}

export function shouldRevalidatePreviewProjectionForImportSessionUpdate(
  event: Pick<ImportSessionUpdatedEvent, 'reason'>,
) {
  return PREVIEW_PROJECTION_REASONS.has(event.reason)
}

export function shouldRevalidateStatusProjectionForImportSessionUpdate(
  event: Pick<ImportSessionUpdatedEvent, 'reason'>,
) {
  return STATUS_PROJECTION_REASONS.has(event.reason)
}

export function shouldRevalidateContextMaterialsProjectionForImportSessionUpdate(
  event: Pick<ImportSessionUpdatedEvent, 'reason'>,
) {
  return CONTEXT_MATERIALS_PROJECTION_REASONS.has(event.reason)
}

export function shouldRevalidateWorkbookProjectionForImportSessionUpdate(
  event: Pick<ImportSessionUpdatedEvent, 'reason'>,
) {
  return WORKBOOK_PROJECTION_REASONS.has(event.reason)
}

export function shouldCoalesceImportSessionUpdate(
  event: Pick<ImportSessionUpdatedEvent, 'reason' | 'kind'>,
) {
  return (
    event.kind === 'machine_progress' &&
    COALESCED_MACHINE_PROGRESS_REASONS.has(event.reason)
  )
}

export function buildImportSessionUpdateDedupKey(
  event: Pick<
    ImportSessionUpdatedEvent,
    'sessionId' | 'machineVersion' | 'reason' | 'requestId' | 'timestamp'
  >,
) {
  return [
    event.sessionId,
    event.machineVersion ?? 'na',
    event.reason,
    event.requestId ?? 'na',
    event.timestamp,
  ].join(':')
}
